
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Globe, ChevronDown } from "lucide-react";
import { useTranslation } from '@/hooks/useTranslation';

interface Language {
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'sw', name: 'Kiswahili', flag: '🇰🇪' },
  { code: 'ha', name: 'Hausa', flag: '🇳🇬' },
  { code: 'yo', name: 'Yorùbá', flag: '🇳🇬' },
  { code: 'am', name: 'አማርኛ (Amharic)', flag: '🇪🇹' },
  { code: 'zu', name: 'isiZulu', flag: '🇿🇦' },
  { code: 'tw', name: 'Twi', flag: '🇬🇭' },
  { code: 'kpe', name: 'Kpelle', flag: '🇱🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
];

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = async (language: Language) => {
    try {
      await i18n.changeLanguage(language.code);
      setIsOpen(false);
      console.log(`Language changed to: ${language.name} (${language.code})`);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 h-8 px-2 text-xs"
      >
        <Globe className="w-3 h-3" />
        <span>{currentLanguage.flag}</span>
        <span className="hidden sm:inline text-xs">{currentLanguage.name}</span>
        <ChevronDown className="w-2 h-2" />
      </Button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <Card className="absolute top-full right-0 mt-1 z-50 min-w-[180px] max-h-64 overflow-y-auto bg-white shadow-lg border">
            <CardContent className="p-1">
              {languages.map((language) => (
                <Button
                  key={language.code}
                  variant={currentLanguage.code === language.code ? "secondary" : "ghost"}
                  onClick={() => handleLanguageChange(language)}
                  className="w-full justify-start text-xs h-8 px-2"
                >
                  <span className="mr-2">{language.flag}</span>
                  <span className="text-xs">{language.name}</span>
                </Button>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
